export type GameLogAction =
    | 'start'
    | 'draw'
    | 'move'
    | 'lock'
    | 'unlock'
    | 'card-lock'
    | 'card-unlock'
    | 'throw'
    | 'draw-card'
    | 'discard'
    | 'stalemate'
    | 'win'
    | 'draw-game'
    | 'restart';

export type GameLogEntry = {
    move: number;
    player: number; // 0 = game-level entry
    action: GameLogAction;
    detail: string;
    bot?: boolean; // true when the action was performed by the bot player
};

const COLUMNS = ['a', 'b', 'c'];

/** Chess-style cell name: columns a-c, rows 1-3 (row 1 = top). */
export const cellName = (row: number, col: number): string =>
    `${COLUMNS[col]}${row + 1}`;

/** Plain-text chess-score line for an entry. */
export const formatEntry = (entry: GameLogEntry): string => {
    const who = entry.player === 0 ? '' : `P${entry.player}${entry.bot ? ' (bot)' : ''} · `;
    return `  ${who}${entry.detail}`;
};

/**
 * Compact 3x3 board diagram. `board` is row-major occupancy
 * (player number or null); `lockedCells` is a list of "row,col" keys.
 * A trailing `*` marks a locked cell.
 */
export const renderBoard = (
    board: (number | null)[][],
    lockedCells: string[]
): string => {
    const lines = ['     a   b   c'];
    for (let row = 0; row < 3; row++) {
        const cells = board[row].map((occupant, col) => {
            const mark = occupant === null ? '.' : `P${occupant}`;
            return lockedCells.includes(`${row},${col}`) ? `${mark}*` : mark;
        });
        lines.push(`  ${row + 1}  ${cells.join('  ')}`);
    }
    return lines.join('\n');
};

/** Full plain-text log file content, grouped by move. */
export const serializeLog = (entries: GameLogEntry[]): string => {
    const lines: string[] = ['TAYTAYA MOVE HISTORY', '========================'];
    let lastMove = 0;
    for (const entry of entries) {
        if (entry.move !== lastMove) {
            lastMove = entry.move;
            lines.push('');
            lines.push(`Move ${entry.move}`);
        }
        lines.push(formatEntry(entry));
    }
    lines.push('');
    lines.push(`End of log — ${entries.length} entry(ies).`);
    return lines.join('\n');
};
